import { Box, Typography } from '@mui/material'
import { DataGrid, type GridColumns, GridOverlay } from '@mui/x-data-grid'
import React, { useMemo } from 'react'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { ActivatePrivateNetworkIntegrationClientButton } from 'components/configurations/private-network-integrations/ActivatePrivateNetworkIntegrationClientButton'
import { DeactivatePrivateNetworkIntegrationButton } from 'components/configurations/private-network-integrations/DeactivatePrivateNetworkIntegrationButton'
import useAuth from 'hooks/use-auth'
import { type IntegrationClientsResponse } from 'queries/integration-clients/use-get-integration-clients'
import { ClientState } from 'types/api/attacks/client'
import { type PrivateNetworkIntegrationClient } from 'types/api/integration-clients/integration-clients'
import { type Team } from 'types/api/teams'
import {
  integrationClientsIdActivatePut,
  integrationClientsIdDelete
} from 'utils/endpoints-access'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles
} from 'utils/mui-components/data-grid-utils'

function getPrivateNetworkIntegrationDisplayStatus(state: ClientState) {
  switch (state) {
    case ClientState.Active:
      return 'Active'
    case ClientState.Revoked:
      return 'Deactivated'
    default:
      return 'Idle'
  }
}

const PrivateNetworkIntegrationsListEmptyState: React.FC = () => {
  return (
    <GridOverlay>
      <Typography>No Private Network Integrations found</Typography>
    </GridOverlay>
  )
}

export const PrivateNetworkIntegrationsList: React.FC<{
  integrationClients: IntegrationClientsResponse
  teams: Team[]
}> = ({ integrationClients, teams }) => {
  const { companyPrefs, hasEndpointAccessForAll } = useAuth()
  const { pni_scope: pniScope } = companyPrefs

  const rows = useMemo(() => {
    const { active, idle, inactive } = integrationClients

    return [...active, ...inactive, ...idle]
  }, [integrationClients])

  const columns: GridColumns<PrivateNetworkIntegrationClient> = [
    {
      ...defaultColumnProps,
      field: 'state',
      headerName: 'Status',
      flex: 0.75,
      renderCell: ({ row }) => {
        const isActive = row.state === ClientState.Active
        const status = getPrivateNetworkIntegrationDisplayStatus(row.state)

        return (
          <Typography
            color={isActive ? 'success.main' : 'text.primary.main'}
            variant="inherit"
          >
            {status}
          </Typography>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'identifier',
      headerName: 'Identifier',
      flex: 1.75,
      renderCell: ({ row }) => (
        <TruncatedTextWithTooltip
          tooltipProps={{ title: row.identifier }}
          variant="inherit"
        >
          {row.identifier}
        </TruncatedTextWithTooltip>
      )
    },
    {
      ...defaultColumnProps,
      field: 'orgId',
      headerName: 'Owning Team',
      flex: 1.75,
      renderCell: ({ row }) => {
        const teamName = teams.find(
          (team) => team.identifier === row.orgId
        )?.name

        return (
          <TruncatedTextWithTooltip
            tooltipProps={{ title: teamName }}
            variant="inherit"
          >
            {teamName}
          </TruncatedTextWithTooltip>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'sharedAcross',
      headerName: 'Shared Across',
      flex: 0.75,
      renderCell: () => <Typography variant="inherit">{pniScope}</Typography>
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: ({ row }) => {
        if (
          !hasEndpointAccessForAll([
            integrationClientsIdDelete,
            integrationClientsIdActivatePut
          ])
        ) {
          return null
        }

        if (row.state === ClientState.Active) {
          return (
            <DeactivatePrivateNetworkIntegrationButton
              integrationClient={row}
            />
          )
        }

        if (row.state === ClientState.Revoked) {
          return (
            <ActivatePrivateNetworkIntegrationClientButton
              integrationClient={row}
            />
          )
        }

        // Idle
        return null
      }
    }
  ]

  return (
    <Box data-testid="private-network-integrations-list">
      <DataGrid
        {...defaultGridProps}
        columns={columns}
        components={{
          NoRowsOverlay: PrivateNetworkIntegrationsListEmptyState
        }}
        getRowId={(value) => value.identifier}
        rows={rows}
        sx={getGridStyles()}
      />
    </Box>
  )
}
